<template>
    <div class="d-flex flex-column">
        <div
            data-t="input"
            :class="containerClass"
            class="position-relative"
            @click="!disabled && $emit('click')"
        >
            <input
                ref="input"
                :aria-labelledby="showLabel ? labelId : undefined"
                :type="type"
                class="bg-transparent autofill-light transition-duration-25 transition-ease-out outline-none border-0 text-dark placeholder-gray-dark w-100 font--16 lh--20 rounded-sm"
                :class="inputClass"
                style="
                    transition-property: opacity, padding-bottom, padding-top;
                "
                :autocomplete="autocomplete"
                :name="name"
                :placeholder="cookedPlaceholder"
                :disabled="disabled"
                :readonly="readonly"
                :value="value"
                :maxlength="maxlength"
                :size="size == 'xs' ? 7 : 20"
                @input="input"
                @focus="focus"
                @blur="blur"
            />

            <label
                v-if="showLabel"
                :id="labelId"
                class="position-absolute left transition-ease-out transition-duration-25 text-gray-dark pointer-events-none text-truncate overflow-hidden"
                :class="labelClass"
                :style="innerLabelStyle"
                style="
                    transition-property: font-size, padding-top, padding-bottom;
                "
            >
                {{ innerLabel }}
            </label>

            <div
                v-if="hasLeftSlot"
                class="position-absolute top left d-flex justify-content-center align-items-center"
                :style="slotStyle"
                data-t="left-slot"
            >
                <slot name="left" />
            </div>

            <div
                v-if="hasRightSlot"
                class="position-absolute top right d-flex justify-content-center align-items-center"
                :style="slotStyle"
                data-t="right-slot"
            >
                <slot name="right" />
            </div>
        </div>

        <input-status :status="status" />
    </div>
</template>

<script>
import { computed } from "vue"
import { slotHasContent } from "~/lib/vueUtils"
import uniqueId from "lodash/uniqueId"
import InputStatus from "~/components/inputs/InputStatus"

export default {
    props: {
        ...InputStatus.props,

        isFocused: {
            type: Boolean,
            default: false,
        },

        type: {
            type: String,
            default: "text",
            validator: val => ["text", "search", "password"].includes(val),
        },

        readonly: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        name: {
            type: String,
            default: null,
        },

        placeholder: {
            type: String,
            default: "",
        },

        innerLabel: {
            type: String,
            default: "",
        },

        autocomplete: {
            type: String,
            default: "off",
        },

        value: {
            type: String,
            default: null,
        },

        borderless: {
            type: Boolean,
            default: false,
        },

        rounded: {
            type: Boolean,
            default: true,
        },

        maxlength: {
            type: [String, Number],
            default: null,
        },

        size: {
            type: String,
            default: "regular",
            validator: val => ["regular", "small", "xs"].includes(val),
        },

        background: {
            type: String,
            default: "light",
        },
    },

    emits: ["click", "update:value", "mounted", "focus", "blur"],

    setup(_props, { slots }) {
        const hasLeftSlot = computed(() => slotHasContent(slots.left))
        const hasRightSlot = computed(() => slotHasContent(slots.right))
        return { hasLeftSlot, hasRightSlot }
    },

    data() {
        return {
            labelId: uniqueId("label"),
            focused: false,
        }
    },

    computed: {
        containerClass() {
            const borderPrimary = this.$css(this.$theme.borderPrimary)
            const status = this.status.code
            return {
                "hover:border-gray-dark": !this.disabled && !status,
                [borderPrimary]: this.focused,
                border: !this.borderless,
                "border-gray-medium": !this.borderless && !status,
                "rounded-lg": this.rounded,
                "border-danger": status == "danger",
                "border-info": status == "info",
                "border-success": status == "success",
                "border-warning": status == "warning",
                "opacity--50": this.disabled,
                [`bg-${this.background}`]: !!this.background,
            }
        },

        labelClass() {
            return {
                "font--16 lh--20 py-15": !(this.focused || this.value),
                "font--12 lh--16 pt-8": this.focused || this.value,

                "pl-44": this.hasLeftSlot,
                "pr-44": this.hasRightSlot,

                "pl-12": !this.hasLeftSlot,
                "pr-12": !this.hasRightSlot,
            }
        },

        inputClass() {
            return {
                "opacity--0": this.innerLabel && !(this.focused || this.value),
                "opacity--100": this.innerLabel && (this.focused || this.value),

                "py-15": !this.innerLabel && this.size == "regular",
                "pt-22 pb-8": this.innerLabel && this.size == "regular",

                "py-8": !this.innerLabel && this.size == "small",

                "pl-44": this.hasLeftSlot,
                "pr-44": this.hasRightSlot,

                "pl-12": !this.hasLeftSlot,
                "pr-12": !this.hasRightSlot,
            }
        },

        cookedPlaceholder() {
            if (!this.innerLabel) return this.placeholder

            if (this.focused) {
                return this.placeholder
            } else {
                return ""
            }
        },

        innerLabelStyle() {
            switch (this.size) {
                case "regular":
                    return "top: 0;"
                case "small":
                    return "top: -9px;"
                case "xs":
                    return ""
                default:
                    throw new Error(`Unexpected size ${this.size}`)
            }
        },

        slotStyle() {
            switch (this.size) {
                case "regular":
                    return "height: 50px; width: 50px;"
                case "small":
                    return "height: 40px; width: 40px;"
                case "xs":
                    return "height: 27px; width: 27px;"
                default:
                    throw new Error(`Unexpected size ${this.size}`)
            }
        },

        showLabel() {
            return this.innerLabel && this.size !== "xs"
        },
    },

    watch: {
        isFocused() {
            this.isFocused ? this.$refs.input.focus() : this.$refs.input.blur()
        },
    },

    mounted() {
        if (this.$refs.input == document.activeElement) {
            this.focused = true
        }
        this.$emit("mounted")
    },

    methods: {
        setFocus() {
            this.$refs.input.focus()
        },

        input(event) {
            this.$emit("update:value", event.target.value)
        },

        focus() {
            this.focused = true
            this.$emit("focus")
        },

        blur() {
            this.focused = false
            this.$emit("blur")
        },
    },

    components: {
        InputStatus,
    },
}
</script>

<style scoped>
.py-15 {
    padding-top: 15px !important;
    padding-bottom: 15px !important;
}
</style>
